/**
 * What finishing a task does besides changing its status - one place for completeTask / cancelTask,
 * the bulk ops and closeContainer, so a task closed in bulk (or together with its project) is not
 * treated differently from one closed on its own:
 *
 *   - a running timer is stopped (a completed / canceled task is skipped by useActiveTimers, so the
 *     timer would run on where nobody can stop it)
 *   - completed: a recurrence instance records its outcome and streak and, for AFTER_COMPLETION,
 *     generates the next instance (completeRecurringInstance)
 *   - completed: a routine step unlocks the next step (advanceRoutineChain)
 *
 * Canceling a task only stops the timer (as cancelTask always did; skipping an instance is
 * skipRecurrenceInstance). The status itself is written by the caller, before the hooks run: they
 * read the stored row.
 */

import type { DbObject } from "./db-types.js"

export type FinishOutcome = "completed" | "canceled"

/** Whether finishing `row` has any hook to run (so a bulk caller can skip the rest cheaply). */
export function needsCompletionHooks(row: Pick<DbObject, "timerStartedAt" | "isRecurrenceInstance" | "recurringTemplateId" | "routineId">): boolean {
  return !!row.timerStartedAt || !!(row.isRecurrenceInstance && row.recurringTemplateId) || !!row.routineId
}

/**
 * Run the hooks for `row` as it was BEFORE its status changed (the flags tell what applies). The
 * status is already written.
 */
export async function runCompletionHooks(row: DbObject, outcome: FinishOutcome): Promise<void> {
  const id = row.id as string
  // Auto-stop a running timer on completion / cancellation
  if (row.timerStartedAt) {
    const { stopTimer } = await import("./timeTrackingOps.js")
    await stopTimer(id)
  }
  if (outcome !== "completed") return

  // A recurring instance: streak + after-completion generation
  if (row.isRecurrenceInstance && row.recurringTemplateId) {
    // Dynamic import avoids a circular dependency at module level
    const { completeRecurringInstance } = await import("./recurrenceOps.js")
    await completeRecurringInstance(id)
  }

  // A routine step: unlock the next one
  if (row.routineId) {
    const { advanceRoutineChain } = await import("./routineOps.js")
    await advanceRoutineChain(id)
  }
}

/**
 * Run the hooks for each of `rows` that has any, one after another. A failing hook does not stop the
 * others (the statuses are written already, there is nothing to undo); the failures are returned so
 * the caller can report them.
 */
export async function runCompletionHooksFor(
  rows: readonly DbObject[],
  outcome: FinishOutcome,
): Promise<Array<{ id: string; error: string }>> {
  const failures: Array<{ id: string; error: string }> = []
  for (const row of rows) {
    if (!needsCompletionHooks(row)) continue
    try {
      await runCompletionHooks(row, outcome)
    } catch (e) {
      const error = e instanceof Error ? e.message : String(e)
      console.error(`[completion hooks] ${outcome} ${row.id}: ${error}`)
      failures.push({ id: row.id as string, error })
    }
  }
  return failures
}
